import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import ControlButtons from './ControlButtons';
import SeatLegend from './SeatLegend';
import { ArrowUp } from 'react-feather';
import { SEAT_COLORS, SEAT_LABEL_COLORS } from './seatPalette';
import { fitToSeats, IDENTITY_TRANSFORM } from './fitToSeats';

// Konfigurasi rentang sudut per section (disesuaikan dengan layout gambar)
const ARC_CONFIG = {
  left: { start: 180, end: -5 },
  right_circle: { start: 175, end: 0 }, // busur kanan (dibalik agar urutan visual naik)
  right: { start: 85, end: -90 }, // busur kanan
  bottom: { start: 100, end: 320 }, // busur bawah (kalau nanti dipakai)
};

// ViewBox tetap supaya layout melingkar muat rapi.
const VIEW_BOX = { x: -200, y: -300, w: 1000, h: 2800 };

// Helper untuk mengubah data kursi jadi posisi circular
const transformSeatDataToCircular = (rawSeats) => {
  if (!rawSeats || rawSeats.length === 0) return [];

  const seats = [...rawSeats];

  // Ambil x_value minimum sebagai referensi radius
  const xValues = seats
    .map((s) => (typeof s.x_value === 'number' ? s.x_value : null))
    .filter((v) => v !== null);

  const minXValue = xValues.length ? Math.min(...xValues) : 0;

  // 👉 Jarak dan posisi ring
  const baseRadius = 1300; // ring terdepan
  const rowSpacing = 30; // jarak antar ring (lebih longgar)

  // Titik pusat lingkaran
  const centerX = 300;
  const centerY = 260; // agak ke atas supaya busur turun ke bawah

  // Group per baris (x). Urutan di dalam group tidak berpengaruh karena yang
  // dipakai hanya nilai y minimum dan maksimum.
  const groupedByRow = seats.reduce((acc, seat) => {
    const key = seat.x;
    if (!acc[key]) acc[key] = [];
    acc[key].push(seat);

    return acc;
  }, {});

  return seats.map((seat) => {
    const locKey = (seat.loc2stage || '').toLowerCase();
    const arcConf = ARC_CONFIG[locKey];

    // Kalau tidak ada arc untuk loc2stage ini, biarkan null (pakai grid)
    if (!arcConf) {
      return {
        ...seat,
        circular_x: null,
        circular_y: null,
        circular_angle: null,
        circular_radius: null,
      };
    }

    const rowSeats = groupedByRow[seat.x] || [];

    const ys = rowSeats.map((s) => s.y);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // Posisi relatif dalam satu baris (0–1)
    const t = maxY === minY ? 0.5 : (seat.y - minY) / (maxY - minY);

    // Sudut sesuai arc section
    const angleDeg = arcConf.start + t * (arcConf.end - arcConf.start);
    const angleRad = (angleDeg * Math.PI) / 180;

    // Radius: makin besar x_value → makin jauh
    const xVal = typeof seat.x_value === 'number' ? seat.x_value : minXValue;

    const radius = baseRadius + (xVal - minXValue) * rowSpacing;

    // 👉 Y pakai PLUS supaya busur melengkung ke bawah
    const finalX = centerX + radius * Math.cos(angleRad);
    const finalY = centerY + radius * Math.sin(angleRad);

    return {
      ...seat,
      circular_x: finalX,
      circular_y: finalY,
      circular_angle: angleDeg,
      circular_radius: radius,
    };
  });
};

const Seat = ({ seatData, filter, selectSeatIds }) => {
  const seats = useMemo(
    () => (Array.isArray(seatData) ? seatData : []),
    [seatData],
  );
  // Di-memo karena jadi dependensi effect auto-fokus.
  const selectedIds = useMemo(
    () => (Array.isArray(selectSeatIds) ? selectSeatIds : []),
    [selectSeatIds],
  );

  const filteredSeats = useMemo(
    () =>
      seats.filter(
        (seat) =>
          seat &&
          (seat.ticket_id === Number(filter) ||
            seat.ticket_id === 9999 ||
            seat.ticket_id === 8888),
      ),
    [seats, filter],
  );

  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const pinchDistanceRef = useRef(null);
  const fittedTransform = useRef(null);

  const [dragging, setDragging] = useState(false);
  const seatSize = 20;
  const seatPadding = 20;
  const totalSize = seatSize + seatPadding * 2;

  const [transform, setTransform] = useState(IDENTITY_TRANSFORM);

  // Hasil transform circular untuk semua kursi yang difilter
  const transformedSeats = useMemo(
    () => transformSeatDataToCircular(filteredSeats),
    [filteredSeats],
  );

  const isEventInContainer = useCallback((clientX, clientY) => {
    if (!containerRef.current) return false;
    const rect = containerRef.current.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }, []);

  const getDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const startDrag = (x, y) => {
    draggingRef.current = true;
    dragStartRef.current = { x, y };
    setDragging(true);
  };

  const stopDrag = () => {
    draggingRef.current = false;
    pinchDistanceRef.current = null;
    setDragging(false);
  };

  // Zoom terhadap satu titik fokus (kursor, atau titik tengah pinch).
  const zoomAt = useCallback((scaleChange, clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const midX = clientX - rect.left;
    const midY = clientY - rect.top;

    setTransform((prev) => {
      const newScale = Math.max(0.5, Math.min(prev.scale * scaleChange, 10));
      const focusX = (midX - prev.translateX) / prev.scale;
      const focusY = (midY - prev.translateY) / prev.scale;

      return {
        scale: newScale,
        translateX: midX - focusX * newScale,
        translateY: midY - focusY * newScale,
      };
    });
  }, []);

  const handleTouchStart = (e) => {
    if (!e.touches.length) return;
    if (!isEventInContainer(e.touches[0].clientX, e.touches[0].clientY)) return;
    e.preventDefault();

    if (e.touches.length === 1) {
      startDrag(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      pinchDistanceRef.current = getDistance(e.touches[0], e.touches[1]);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrag();
  };

  // Listener non-passive dipasang sekali saja. Status drag/pinch disimpan di
  // ref supaya effect ini tidak dipasang ulang setiap frame zoom/pan.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleWheel = (e) => {
      if (!isEventInContainer(e.clientX, e.clientY)) return;

      e.preventDefault();
      e.stopPropagation();

      const zoomIntensity = 0.1;
      zoomAt(
        e.deltaY < 0 ? 1 + zoomIntensity : 1 - zoomIntensity,
        e.clientX,
        e.clientY,
      );
    };

    const handleTouchMove = (e) => {
      e.preventDefault();

      if (e.touches.length === 1 && draggingRef.current) {
        const slowFactor = 3;

        const deltaX =
          (e.touches[0].clientX - dragStartRef.current.x) / slowFactor;
        const deltaY =
          (e.touches[0].clientY - dragStartRef.current.y) / slowFactor;

        setTransform((prev) => ({
          ...prev,
          translateX: prev.translateX + deltaX,
          translateY: prev.translateY + deltaY,
        }));

        dragStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
      } else if (e.touches.length === 2 && pinchDistanceRef.current) {
        const distance = getDistance(e.touches[0], e.touches[1]);
        zoomAt(
          distance / pinchDistanceRef.current,
          (e.touches[0].clientX + e.touches[1].clientX) / 2,
          (e.touches[0].clientY + e.touches[1].clientY) / 2,
        );
        pinchDistanceRef.current = distance;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isEventInContainer, zoomAt]);

  const handleMouseDown = (e) => {
    if (!isEventInContainer(e.clientX, e.clientY)) return;
    e.preventDefault();

    if (
      e.target.tagName === 'svg' ||
      e.target.tagName === 'rect' ||
      e.target.tagName === 'text'
    ) {
      startDrag(e.clientX, e.clientY);
    }
  };

  const handleMouseMove = (e) => {
    if (!draggingRef.current) return;
    if (!isEventInContainer(e.clientX, e.clientY)) return;
    e.preventDefault();

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    setTransform((prev) => ({
      ...prev,
      translateX: prev.translateX + deltaX,
      translateY: prev.translateY + deltaY,
    }));
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    stopDrag();
  };

  const colorSeat = (seat) => {
    if (seat.ticket_id === 8888) return SEAT_COLORS.reference;
    if (selectedIds.includes(seat.id)) return SEAT_COLORS.yours;
    if (Number(seat.status) !== 1) return SEAT_COLORS.unavailable;
    // Berbayar = ada biaya tambahan sama sekali. Ambang 100.000 sebelumnya
    // salah menandai kursi 50.000 dan 80.000 sebagai gratis.
    return Number(seat.seat_charged) > 0
      ? SEAT_COLORS.paid
      : SEAT_COLORS.available;
  };

  const seatCursor = (seat) =>
    Number(seat.status) === 1 ? 'pointer' : 'not-allowed';

  const getRowIndex = (row) => {
    let index = 0;
    for (let i = 0; i < row.length; i++) {
      index = index * 26 + (row.charCodeAt(i) - 65 + 1);
    }
    return index - 1;
  };

  const handleZoomIn = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.min(prev.scale + 0.1, 10),
    }));
  };

  const handleZoomOut = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.max(prev.scale - 0.1, 0.5),
    }));
  };

  const handleReset = () => {
    setTransform(fittedTransform.current ?? IDENTITY_TRANSFORM);
  };

  // Posisi kotak kursi dalam satuan viewBox. Kursi pada busur memakai
  // circular_x/y; sisanya jatuh kembali ke grid lama.
  const seatRect = useCallback(
    (seat) => {
      const isCircular = !!ARC_CONFIG[(seat.loc2stage || '').toLowerCase()];
      return {
        x: isCircular
          ? seat.circular_x - seatSize / 2
          : (seat.y - 1) * totalSize + seatPadding,
        y: isCircular
          ? seat.circular_y - seatSize / 2
          : getRowIndex(seat.x) * totalSize + seatPadding + 100,
      };
    },
    [totalSize],
  );

  // Saat denah dibuka, langsung arahkan dan perbesar ke kursi pelanggan.
  // ViewBox di sini setinggi 2800 satuan, jadi tanpa ini kursi pelanggan
  // hampir pasti berada di luar layar saat pertama dibuka.
  useEffect(() => {
    if (fittedTransform.current) return undefined;

    const targets = transformedSeats.filter(
      (seat) => seat.ticket_id !== 9999 && selectedIds.includes(seat.id),
    );
    if (!targets.length) return undefined;

    let frame = 0;
    let attempts = 0;

    const fit = () => {
      const box = containerRef.current?.getBoundingClientRect();
      if (!box?.width || !box?.height) {
        if (attempts++ < 20) frame = requestAnimationFrame(fit);
        return;
      }

      const next = fitToSeats({
        viewBox: VIEW_BOX,
        box,
        rects: targets.map(seatRect),
        seatSize,
      });
      if (!next) return;

      fittedTransform.current = next;
      setTransform(next);
    };

    frame = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(frame);
  }, [transformedSeats, selectedIds, seatRect]);

  const viewBoxWidth = VIEW_BOX.w;
  const viewBoxHeight = VIEW_BOX.h;

  return (
    <>
      <SeatLegend />
      <p className="seat-map-hint">
        Denah sudah diarahkan ke kursi Anda (bertanda merah). Cubit atau pakai
        tombol +/− untuk memperbesar, geser untuk berpindah.
      </p>
      <h6 className="seat-map-stage">Panggung</h6>
      <div className="seat-map-frame">
        <div
          ref={containerRef}
          style={{
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            cursor: dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {transformedSeats.length === 0 ? (
            <p className="seat-map-empty fw-lighter">
              Denah kursi tidak tersedia untuk kategori ini.
            </p>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="seat-map-canvas"
              viewBox={`${VIEW_BOX.x} ${VIEW_BOX.y} ${viewBoxWidth} ${viewBoxHeight}`}
              preserveAspectRatio="xMidYMid"
              style={{
                transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
                transformOrigin: '0 0',
              }}
            >
              {transformedSeats.map((seat) => {
                const { x: rectX, y: rectY } = seatRect(seat);

                if (seat.ticket_id === 9999) {
                  return null;
                }

                const isSelected = selectedIds.includes(seat?.id);
                const cursor = seatCursor(seat);

                return (
                  <g
                    key={seat.id}
                    data-seat={seat.name}
                    style={{ pointerEvents: 'all' }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {isSelected && (
                      <rect
                        x={rectX - 4}
                        y={rectY - 4}
                        width={seatSize + 8}
                        height={seatSize + 8}
                        rx={5}
                        fill="none"
                        stroke={SEAT_COLORS.yours}
                        strokeOpacity="0.45"
                        strokeWidth="2.5"
                      />
                    )}
                    <rect
                      x={rectX}
                      y={rectY}
                      width={seatSize}
                      height={seatSize}
                      rx={3}
                      strokeWidth={isSelected ? 1.8 : 0.6}
                      stroke={isSelected ? '#fff' : 'rgba(0, 0, 0, 0.45)'}
                      fill={colorSeat(seat)}
                      style={{ cursor }}
                    />

                    {seat.ticket_id !== 8888 && (
                      <text
                        x={rectX + seatSize / 2}
                        y={rectY + seatSize / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="7px"
                        fontWeight="700"
                        fill={
                          isSelected
                            ? SEAT_LABEL_COLORS.onDark
                            : SEAT_LABEL_COLORS.onLight
                        }
                        style={{ cursor }}
                      >
                        {seat.name}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>
      <ControlButtons
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
      />
      <div className="seat-map-direction">
        <ArrowUp size={18} aria-hidden="true" />
        <span>Arah hadap kursi</span>
        <ArrowUp size={18} aria-hidden="true" />
      </div>
    </>
  );
};

export default Seat;
